import clsx from "clsx";
import * as React from "react";

type BreadcrumbItem =
  | string
  | {
      label: string;
      href: string;
    };
type BreadCrumbsItems = BreadcrumbItem[];

interface BreadcrumbProps extends React.HTMLAttributes<HTMLDivElement> {
  data: BreadCrumbsItems;
  separator?: "line" | "arrow" | "slash";
  classNames?: {
    root?: string;
    item?: string;
    separator?: string;
  };
}

const Breadcrumb = React.forwardRef<HTMLDivElement, BreadcrumbProps>(
  (
    { children, separator = "|", data, className, classNames, ...rest },
    ref,
  ) => {
    const parsedSeparator =
      separator === "line" ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          fill="currentColor"
          viewBox="0 0 256 256"
        >
          <path d="M136,24V232a8,8,0,0,1-16,0V24a8,8,0,0,1,16,0Z"></path>
        </svg>
      ) : separator === "slash" ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          fill="currentColor"
          viewBox="0 0 21 21"
        >
          <path
            stroke="currentColor"
            strokeLinejoin="round"
            d="M12.5 3.5l-4 14"
          ></path>
        </svg>
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          fill="currentColor"
          viewBox="0 0 256 256"
        >
          <path d="M181.66 133.66l-80 80a8 8 0 01-11.32-11.32L164.69 128 90.34 53.66a8 8 0 0111.32-11.32l80 80a8 8 0 010 11.32z"></path>
        </svg>
      );

    const fontClass = "ox-fz-sm";

    const renderBreadcrumbs = () => {
      if (data.length <= 5) {
        return data.map((item, index) => {
          if (typeof item === "string") {
            return (
              <React.Fragment key={item}>
                <span
                  className={clsx(
                    "ox-reset ox-BreadcrumbItem",
                    fontClass,
                    classNames?.item,
                  )}
                  key={index}
                >
                  {item}
                </span>
                {index < data.length - 1 ? (
                  <span
                    className={clsx(
                      "ox-reset ox-BreadcrumbSeparator",
                      classNames?.separator,
                    )}
                  >
                    {parsedSeparator}
                  </span>
                ) : null}
              </React.Fragment>
            );
          } else {
            return (
              <React.Fragment key={index}>
                <a
                  className={clsx(
                    "ox-reset ox-BreadcrumbItem",
                    fontClass,
                    classNames?.item,
                  )}
                  href={item.href}
                  key={index}
                >
                  {item.label}
                </a>
                {index < data.length - 1 ? (
                  <span
                    className={clsx(
                      "ox-reset ox-BreadcrumbSeparator",
                      classNames?.separator,
                    )}
                  >
                    {parsedSeparator}
                  </span>
                ) : null}
              </React.Fragment>
            );
          }
        });
      } else {
        const visibleItems = [data[0], "...", ...data.slice(data.length - 4)];
        return visibleItems.map((item, index) => {
          if (typeof item === "string") {
            return (
              <React.Fragment key={index}>
                <span
                  className={clsx(
                    "ox-reset ox-BreadcrumbItem",
                    fontClass,
                    classNames?.item,
                  )}
                  key={index}
                >
                  {item}
                </span>
                {index < visibleItems.length - 1 ? (
                  <span
                    className={clsx(
                      "ox-reset ox-BreadcrumbSeparator",
                      classNames?.separator,
                    )}
                  >
                    {parsedSeparator}
                  </span>
                ) : null}
              </React.Fragment>
            );
          } else {
            return (
              <React.Fragment key={index}>
                <a
                  className={clsx(
                    "ox-reset ox-BreadcrumbItem",
                    fontClass,
                    classNames?.item,
                  )}
                  href={item.href}
                  key={index}
                >
                  {item.label}
                </a>
                {index < data.length - 1 ? (
                  <span
                    className={clsx(
                      "ox-reset ox-BreadcrumbSeparator",
                      classNames?.separator,
                    )}
                  >
                    {parsedSeparator}
                  </span>
                ) : null}
              </React.Fragment>
            );
          }
        });
      }
    };

    return (
      <div
        ref={ref}
        className={clsx("ox-reset ox-Breadcrumb", className, classNames?.root)}
        {...rest}
      >
        {renderBreadcrumbs()}
      </div>
    );
  },
);

Breadcrumb.displayName = "Breadcrumb";
export { Breadcrumb };
export type { BreadcrumbProps };
